import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import * as S from '../BarChart/styled'
import { ptBR, enUS, es, zhCN } from 'date-fns/locale'
import { format } from 'date-fns'
import { convertToTimeZone } from 'date-fns-timezone'
import { useEffect, useState, useMemo } from 'react'
import { CircularProgress } from '@material-ui/core'
import { UserStorageService } from 'services'
import { USER_ROLES } from 'constants/user.constants'

window.Highcharts = Highcharts

export type InvoiceByMonthData = {
  name: string
  y: number
  month: string
  valor: string
  innatura: string
  innatura_rastreavel: string
  innatura_parcial: string
  innatura_pendente: string
  innatura_nao_rastreavel: string
  processados: string
  processados_rastreavel: string
  processados_parcial: string
  processados_nao_rastreavel: string
  processados_pendente: string
}

export type StackedBarChartProps = {
  data: InvoiceByMonthData[]
  height?: number
  width?: number
  isLoading?: boolean
  xAxisTitle?: string
  yAxisTitle?: string
  labels?: {
    inNatura?: string
    inNaturaRastreada?: string
    inNaturaParcial?: string
    inNaturaNaoRastreada?: string
    inNaturaPendente?: string
    industrializados?: string
    industrializadosRastreada?: string
    industrializadosParcial?: string
    industrializadosNaoRastreada?: string
    industrializadosPendente?: string
  }
}

const DEFAULT_LABELS = {
  inNatura: 'IN NATURA',
  inNaturaRastreada: 'Rastreada',
  inNaturaParcial: 'Parcial',
  inNaturaNaoRastreada: 'Não rastreada',
  inNaturaPendente: 'Pendente',
  industrializados: 'INDUSTRIALIZADOS',
  industrializadosRastreada: 'Rastreada',
  industrializadosParcial: 'Parcial',
  industrializadosNaoRastreada: 'Não rastreada',
  industrializadosPendente: 'Pendente'
}

const CHART_COLORS = {
  // In Natura - tons de azul
  inNaturaRastreada: '#33CCCC',
  inNaturaParcial: '#4a6fa5',
  inNaturaNaoRastreada: '#d88a90',
  inNaturaPendente: '#FFCC99',
  // Industrializados - tons mais escuros
  industrializadosRastreada: '#009999',
  industrializadosParcial: '#2d4a6f',
  industrializadosNaoRastreada: '#c45a65',
  industrializadosPendente: '#FF9900'
}

export function StackedBarChart({
  data,
  height = 400,
  // width,
  isLoading = false,
  xAxisTitle = '',
  yAxisTitle = '',
  labels = {}
}: StackedBarChartProps) {
  const [chartLang, setChartLang] = useState(ptBR)
  const mergedLabels = useMemo(
    () => ({ ...DEFAULT_LABELS, ...labels }),
    [labels]
  )
  const { user } = UserStorageService.getUserData()
  const isAdmin = user.tag === USER_ROLES.admin
  useEffect(() => {
    const lang = window.localStorage.getItem('i18nextLng')
    if (lang === 'pt-BR') setChartLang(ptBR)
    if (lang === 'en-US') setChartLang(enUS)
    if (lang === 'es') setChartLang(es)
    if (lang === 'ch') setChartLang(zhCN)
  }, [data])

  const { categories, series, tooltipData } = useMemo(() => {
    if (isLoading || !data?.length) {
      return { categories: [], series: [], tooltipData: [] }
    }

    const categories = data.map(item =>
      format(
        convertToTimeZone(new Date(item.name), { timeZone: 'UTC' }),
        'MMM',
        { locale: chartLang }
      )
    )

    const tooltipData = data.map(item => ({
      innatura: Number(item.innatura) || 0,
      innatura_rastreavel: Number(item.innatura_rastreavel) || 0,
      innatura_parcial: Number(item.innatura_parcial) || 0,
      innatura_nao_rastreavel: Number(item.innatura_nao_rastreavel) || 0,
      innatura_pendente: Number(item.innatura_pendente) || 0,
      processados: Number(item.processados) || 0,
      processados_rastreavel: Number(item.processados_rastreavel) || 0,
      processados_parcial: Number(item.processados_parcial) || 0,
      processados_nao_rastreavel: Number(item.processados_nao_rastreavel) || 0,
      processados_pendente: Number(item.processados_pendente) || 0
    }))

    // Para admin: 3 colunas por mês (Rastreada, Parcial, Não Rastreada)
    // Para visualizador: 1 coluna com totais (innatura e processados)
    // Legenda agrupada: In Natura e Industrializados juntos por categoria
    const adminSeries = [
      // Coluna 1: Rastreada (In Natura)
      {
        name: `${mergedLabels.inNatura} - ${mergedLabels.inNaturaRastreada}`,
        data: data.map(item => Number(item.innatura_rastreavel) || 0),
        color: CHART_COLORS.inNaturaRastreada,
        stack: 'rastreada',
        legendIndex: 0
      },
      // Coluna 1: Rastreada (Industrializados)
      {
        name: `${mergedLabels.industrializados} - ${mergedLabels.industrializadosRastreada}`,
        data: data.map(item => Number(item.processados_rastreavel) || 0),
        color: CHART_COLORS.industrializadosRastreada,
        stack: 'rastreada',
        legendIndex: 1
      },
      // Coluna 2: Parcial (In Natura)
      {
        name: `${mergedLabels.inNatura} - ${mergedLabels.inNaturaParcial}`,
        data: data.map(item => Number(item.innatura_parcial) || 0),
        color: CHART_COLORS.inNaturaParcial,
        stack: 'parcial',
        legendIndex: 2
      },
      // Coluna 2: Parcial (Industrializados)
      {
        name: `${mergedLabels.industrializados} - ${mergedLabels.industrializadosParcial}`,
        data: data.map(item => Number(item.processados_parcial) || 0),
        color: CHART_COLORS.industrializadosParcial,
        stack: 'parcial',
        legendIndex: 3
      },
      // Coluna 3: Não Rastreada (In Natura)
      {
        name: `${mergedLabels.inNatura} - ${mergedLabels.inNaturaNaoRastreada}`,
        data: data.map(item => Number(item.innatura_nao_rastreavel) || 0),
        color: CHART_COLORS.inNaturaNaoRastreada,
        stack: 'naoRastreada',
        legendIndex: 4
      },
      // Coluna 3: Não Rastreada (Industrializados)
      {
        name: `${mergedLabels.industrializados} - ${mergedLabels.industrializadosNaoRastreada}`,
        data: data.map(item => Number(item.processados_nao_rastreavel) || 0),
        color: CHART_COLORS.industrializadosNaoRastreada,
        stack: 'naoRastreada',
        legendIndex: 5
      },
      // Coluna 4: Pendente (In Natura)
      {
        name: `${mergedLabels.inNatura} - ${mergedLabels.inNaturaPendente}`,
        data: data.map(item => Number(item.innatura_pendente) || 0),
        color: CHART_COLORS.inNaturaPendente,
        stack: 'pendente',
        legendIndex: 6
      },
      // Coluna 4: Pendente (Industrializados)
      {
        name: `${mergedLabels.industrializados} - ${mergedLabels.industrializadosPendente}`,
        data: data.map(item => Number(item.processados_pendente) || 0),
        color: CHART_COLORS.industrializadosPendente,
        stack: 'pendente',
        legendIndex: 7
      }
    ]

    // Séries para visualizador: usa os totais (innatura e processados)
    const viewerSeries = [
      {
        name: mergedLabels.inNatura,
        data: data.map(item => Number(item.innatura) || 0),
        color: CHART_COLORS.inNaturaRastreada,
        stack: 'total',
        legendIndex: 0
      },
      {
        name: mergedLabels.industrializados,
        data: data.map(item => Number(item.processados) || 0),
        color: CHART_COLORS.industrializadosRastreada,
        stack: 'total',
        legendIndex: 1
      }
    ]

    const series = isAdmin ? adminSeries : viewerSeries

    return { categories, series, tooltipData }
  }, [data, chartLang, isLoading, mergedLabels, isAdmin])

  const options: Highcharts.Options = {
    chart: {
      type: 'column',
      height,
      spacingLeft: 0,
      spacingRight: 0,
      marginLeft: 50,
      marginRight: 10
    },
    title: {
      text: ''
    },
    xAxis: {
      categories,
      title: {
        text: xAxisTitle,
        style: { fontSize: '12px', color: '#666' }
      },
      labels: {
        style: { fontSize: '12px', color: '#374151', fontWeight: '500' }
      },
      lineColor: '#e5e7eb',
      tickColor: '#e5e7eb'
    },
    yAxis: {
      min: 0,
      title: {
        text: yAxisTitle,
        style: { fontSize: '12px', color: '#666' }
      },
      labels: {
        style: { fontSize: '11px', color: '#666' }
      },
      gridLineColor: '#e5e7eb',
      stackLabels: {
        enabled: false
      }
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function () {
        const pointIndex = this.points?.[0]?.point?.index ?? 0
        const pointData = tooltipData[pointIndex]
        if (!pointData) return ''

        const formatNumber = (num: number) =>
          num.toLocaleString('pt-BR', { maximumFractionDigits: 0 })

        // Tooltip simplificado para não-admin (totais)
        if (!isAdmin) {
          return `
            <div style="padding: 8px; min-width: 200px;">
              <div style="font-weight: bold; font-size: 14px; margin-bottom: 12px;">${
                this.x
              }</div>

              <div style="margin-bottom: 12px;">
                <div style="display: flex; align-items: center; margin-bottom: 6px;">
                  <span style="width: 10px; height: 10px; background: ${
                    CHART_COLORS.inNaturaRastreada
                  }; border-radius: 50%; margin-right: 8px;"></span>
                  <span style="font-weight: bold; color: #333;">${
                    mergedLabels.inNatura
                  }</span>
                  <span style="margin-left: auto; font-weight: 500;">${formatNumber(
                    pointData.innatura
                  )}</span>
                </div>
              </div>

              <div>
                <div style="display: flex; align-items: center; margin-bottom: 6px;">
                  <span style="width: 10px; height: 10px; background: ${
                    CHART_COLORS.industrializadosRastreada
                  }; border-radius: 50%; margin-right: 8px;"></span>
                  <span style="font-weight: bold; color: #333;">${
                    mergedLabels.industrializados
                  }</span>
                  <span style="margin-left: auto; font-weight: 500;">${formatNumber(
                    pointData.processados
                  )}</span>
                </div>
              </div>
            </div>
          `
        }

        // Tooltip completo para admin
        return `
          <div style="padding: 8px; min-width: 200px;">
            <div style="font-weight: bold; font-size: 14px; margin-bottom: 12px;">${
              this.x
            }</div>

            <div style="margin-bottom: 12px;">
              <div style="display: flex; align-items: center; margin-bottom: 6px;">
                <span style="width: 10px; height: 10px; background: ${
                  CHART_COLORS.inNaturaRastreada
                }; border-radius: 50%; margin-right: 8px;"></span>
                <span style="font-weight: bold; color: #333;">${
                  mergedLabels.inNatura
                }</span>
                <span style="margin-left: auto; color: #999;">(${formatNumber(
                  pointData.innatura
                )})</span>
              </div>
              <div style="padding-left: 18px; font-size: 12px; color: #666;">
                <div style="display: flex; align-items: center; margin-bottom: 3px;">
                  <span style="width: 8px; height: 8px; background: ${
                    CHART_COLORS.inNaturaRastreada
                  }; border-radius: 50%; margin-right: 6px;"></span>
                  ${mergedLabels.inNaturaRastreada}:
                  <span style="margin-left: auto; font-weight: 500;">${formatNumber(
                    pointData.innatura_rastreavel
                  )}</span>
                </div>
                <div style="display: flex; align-items: center; margin-bottom: 3px;">
                  <span style="width: 8px; height: 8px; background: ${
                    CHART_COLORS.inNaturaParcial
                  }; border-radius: 50%; margin-right: 6px;"></span>
                  ${mergedLabels.inNaturaParcial}:
                  <span style="margin-left: auto; font-weight: 500;">${formatNumber(
                    pointData.innatura_parcial
                  )}</span>
                </div>
                <div style="display: flex; align-items: center; margin-bottom: 3px;">
                  <span style="width: 8px; height: 8px; background: ${
                    CHART_COLORS.inNaturaNaoRastreada
                  }; border-radius: 50%; margin-right: 6px;"></span>
                  ${mergedLabels.inNaturaNaoRastreada}:
                  <span style="margin-left: auto; font-weight: 500;">${formatNumber(
                    pointData.innatura_nao_rastreavel
                  )}</span>
                </div>
                <div style="display: flex; align-items: center;">
                  <span style="width: 8px; height: 8px; background: ${
                    CHART_COLORS.inNaturaPendente
                  }; border-radius: 50%; margin-right: 6px;"></span>
                  ${mergedLabels.inNaturaPendente}:
                  <span style="margin-left: auto; font-weight: 500;">${formatNumber(
                    pointData.innatura_pendente
                  )}</span>
                </div>
              </div>
            </div>

            <div>
              <div style="display: flex; align-items: center; margin-bottom: 6px;">
                <span style="width: 10px; height: 10px; background: ${
                  CHART_COLORS.industrializadosRastreada
                }; border-radius: 50%; margin-right: 8px;"></span>
                <span style="font-weight: bold; color: #333;">${
                  mergedLabels.industrializados
                }</span>
                <span style="margin-left: auto; color: #999;">(${formatNumber(
                  pointData.processados
                )})</span>
              </div>
              <div style="padding-left: 18px; font-size: 12px; color: #666;">
                <div style="display: flex; align-items: center; margin-bottom: 3px;">
                  <span style="width: 8px; height: 8px; background: ${
                    CHART_COLORS.industrializadosRastreada
                  }; border-radius: 50%; margin-right: 6px;"></span>
                  ${mergedLabels.industrializadosRastreada}:
                  <span style="margin-left: auto; font-weight: 500;">${formatNumber(
                    pointData.processados_rastreavel
                  )}</span>
                </div>
                <div style="display: flex; align-items: center; margin-bottom: 3px;">
                  <span style="width: 8px; height: 8px; background: ${
                    CHART_COLORS.industrializadosParcial
                  }; border-radius: 50%; margin-right: 6px;"></span>
                  ${mergedLabels.industrializadosParcial}:
                  <span style="margin-left: auto; font-weight: 500;">${formatNumber(
                    pointData.processados_parcial
                  )}</span>
                </div>
                <div style="display: flex; align-items: center; margin-bottom: 3px;">
                  <span style="width: 8px; height: 8px; background: ${
                    CHART_COLORS.industrializadosNaoRastreada
                  }; border-radius: 50%; margin-right: 6px;"></span>
                  ${mergedLabels.industrializadosNaoRastreada}:
                  <span style="margin-left: auto; font-weight: 500;">${formatNumber(
                    pointData.processados_nao_rastreavel
                  )}</span>
                </div>
                <div style="display: flex; align-items: center;">
                  <span style="width: 8px; height: 8px; background: ${
                    CHART_COLORS.industrializadosPendente
                  }; border-radius: 50%; margin-right: 6px;"></span>
                  ${mergedLabels.industrializadosPendente}:
                  <span style="margin-left: auto; font-weight: 500;">${formatNumber(
                    pointData.processados_pendente
                  )}</span>
                </div>
              </div>
            </div>
          </div>
        `
      }
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        borderWidth: 0,
        pointPadding: 0.02,
        groupPadding: 0.05,
        dataLabels: {
          enabled: true,
          inside: true,
          style: {
            fontSize: '9px',
            fontWeight: '600',
            color: '#fff',
            textOutline: 'none'
          },
          formatter: function () {
            if (!this.y || this.y === 0) return ''
            // Não mostrar label para valores pequenos (menos de 5% do total da stack)
            const stackTotal = this.total || 0
            if (stackTotal > 0 && this.y < stackTotal * 0.05) return ''
            // Formatar valores acima de 1000 como 1.1K
            if (this.y >= 1000) {
              return (this.y / 1000).toFixed(1) + 'K'
            }
            return String(this.y)
          }
        }
      }
    },
    legend: {
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      itemStyle: {
        fontSize: '12px',
        fontWeight: 'normal'
      },
      itemDistance: 20,
      symbolRadius: 4,
      symbolHeight: 12,
      symbolWidth: 12
    },
    series: series as Highcharts.SeriesOptionsType[],
    credits: { enabled: false }
  }

  return (
    <>
      {isLoading ? (
        <S.BarChartLoadingContainer>
          <S.LoadingTextContainer>
            <CircularProgress size={75} color="primary" />
          </S.LoadingTextContainer>
        </S.BarChartLoadingContainer>
      ) : (
        <div
          style={{
            textTransform: 'capitalize',
            width: '100%'
          }}
        >
          <HighchartsReact highcharts={Highcharts} options={options} />
        </div>
      )}
    </>
  )
}
